import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useMemo } from 'react';

import { useDocumentSurtitle } from '../../hooks/useSurtitle';

import Blocks from '../blocks/Blocks';
import PillButton from '../buttons/PillButton';
import Background from '../partials/Background';
import Image from '../partials/Image';
import Surtitle from '../partials/Surtitle';
import Body from '../typography/Body';
import HeaderDescription from '../typography/HeaderDescription';
import HeaderTitle from '../typography/HeaderTitle';
import IndexDefaultHeader from './IndexDefaultHeader';

import styles from '../../styles/headers/collection-serie-header.module.css';

export interface CollectionSerieHeaderProps {
    type?: string | null;
    title?: string | null;
    surtitle?: string | null;
    subtitle?: string | null;
    description?: string | null;
    background?: Background | null;
    callToAction?: CallToAction | null;
    metadata?: CollectionMetadata | null;
    color?: string;
    image?: Image | null;
    currentDocument?: Document | null;
    placeholder?: boolean;
    withoutImage?: boolean;
    className?: string | null;
    imageClassName?: string | null;
}

function CollectionSerieHeader({
    title = null,
    surtitle = null,
    subtitle = null,
    description = null,
    image = null,
    callToAction = null,
    metadata = null,
    placeholder = false,
    background = null,
    currentDocument = null,
    withoutImage = false,
    className = null,
    imageClassName = null,
}: CollectionSerieHeaderProps) {
    const url = useUrlGenerator();
    const { brands, formats, categories } = metadata || {};
    const [brand = null] = brands || [];
    const { handle: brandHandle } = brand || {};

    const pills = useMemo(() => {
        return [
            ...(categories || []).map(({ label, slug }) => ({
                label,
                href: url('category', { slug }),
            })),
            ...(formats || []).map(({ label, slug }) => ({
                label,
                href: url('format', { slug }),
            })),
        ];
    }, [categories, formats, url]);

    const {
        title: documentTitle = null,
        metadata: documentMetadata,
        blocks: documentBlocks = null,
    } = currentDocument || {};
    const { formats: documentFormats, categories: documentCategories } = documentMetadata || {};
    const documentSurtitle = useDocumentSurtitle(currentDocument, {
        withoutSerie: true,
    });
    const documentPills = useMemo(() => {
        return [
            ...(documentCategories || []).map(({ label, slug }) => ({
                label,
                href: url('category', { slug }),
            })),
            ...(documentFormats || []).map(({ label, slug }) => ({
                label,
                href: url('format', { slug }),
            })),
        ];
    }, [documentCategories, documentFormats, url]);

    const hasOnlyTitle = description === null && background === null && image === null;
    if (hasOnlyTitle && !placeholder) {
        return <IndexDefaultHeader brand={brand} title={title} className={className} />;
    }

    return (
        <div
            className={classNames([
                styles.container,
                styles[brandHandle],
                {
                    [styles.imageWithBackground]: background !== null && image !== null,
                },
                className,
            ])}
        >
            {!withoutImage ? (
                <div className={classNames([styles.imageContainer, imageClassName])}>
                    {background !== null ? (
                        <Background
                            {...background}
                            className={styles.background}
                            imageClassName={styles.backgroundImage}
                            pictureClassName={styles.backgroundPicture}
                        />
                    ) : null}

                    {image !== null ? (
                        <Image
                            className={styles.image}
                            media={image}
                            size="responsive"
                            pictureClassName={styles.picture}
                        />
                    ) : null}
                </div>
            ) : null}
            {currentDocument !== null ? (
                <div className={classNames([styles.content, styles.document])}>
                    <div className={styles.inner}>
                        {documentSurtitle !== null ? (
                            <Surtitle surtitle={documentSurtitle} className={styles.surtitle} />
                        ) : null}
                        <HeaderTitle className={styles.title}>{documentTitle}</HeaderTitle>
                        {documentBlocks !== null ? (
                            <div className={styles.description}>
                                <Blocks blocks={documentBlocks} withoutMaxWidth />
                            </div>
                        ) : null}
                        {documentPills.length > 0 ? (
                            <div className={styles.pills}>
                                {documentPills.map(({ label, href }) => (
                                    <PillButton key={`pill-${href}`} href={href} bordered compact>
                                        {label}
                                    </PillButton>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
            <div className={styles.content}>
                <div className={styles.inner}>
                    <HeaderTitle className={styles.title}>{title}</HeaderTitle>
                    {surtitle !== null ? (
                        <Surtitle surtitle={surtitle} className={styles.surtitle} />
                    ) : null}
                    {description !== null ? (
                        <HeaderDescription html={description} className={styles.description} />
                    ) : null}
                    {pills.length > 0 ? (
                        <div className={styles.pills}>
                            {pills.map(({ label, href }) => (
                                <PillButton key={`pill-${href}`} href={href} bordered compact>
                                    {label}
                                </PillButton>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default CollectionSerieHeader;
